/**
 * Helpers for working with date-only values (YYYY-MM-DD) without timezone shifts.
 *
 * Key idea:
 * - Never use `new Date("YYYY-MM-DD")` for date-only values. Browsers interpret that as UTC,
 *   which will display as the previous/next day in some timezones (e.g. US).
 * - Instead, construct a local Date from components: `new Date(year, monthIndex, day)`.
 */

import { format } from "date-fns";
import type { DateRange } from "react-day-picker";

const YYYY_MM_DD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseYYYYMMDDToLocalDate(value: string): Date {
  const m = YYYY_MM_DD_RE.exec(value);
  if (!m) {
    // Fallback for full ISO datetime strings (e.g. 2026-01-05T12:34:56Z).
    return new Date(value);
  }
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  return new Date(year, monthIndex, day);
}

export function formatLocalDateToYYYYMMDD(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Add calendar days to a YYYY-MM-DD value without UTC round-tripping. */
export function isoDatePlusDays(isoDate: string, daysToAdd: number): string {
  const base = parseYYYYMMDDToLocalDate(isoDate);
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + daysToAdd);
  return formatLocalDateToYYYYMMDD(base);
}

/** Long display label (e.g. March 18, 2026) from a date-only ISO string. */
export function formatISODateLabelLong(isoDate: string): string {
  const dateOnly = isoDate.split("T")[0] ?? isoDate;
  return format(parseYYYYMMDDToLocalDate(dateOnly), "MMMM d, yyyy");
}

/** API filter params — only when the user has picked both ends of a range. */
export function completeDateRangeToFilterISO(
  range: DateRange | undefined
): { from?: string; to?: string } {
  if (!range?.from || !range?.to) return {};
  return {
    from: formatLocalDateToYYYYMMDD(range.from),
    to: formatLocalDateToYYYYMMDD(range.to),
  };
}

export function formatGoalDateRangeLabel(range: DateRange | undefined): string {
  if (!range?.from) return "All time";
  if (!range?.to) return `${format(range.from, "MMM d, yyyy")} - pick end date`;
  return `${format(range.from, "MMM d, yyyy")} - ${format(range.to, "MMM d, yyyy")}`;
}

/** Short chart axis label (e.g. 3/18) from a date-only ISO string. */
export function formatISODateLabelShort(isoDate: string): string {
  const dateOnly = isoDate.split("T")[0] ?? isoDate;
  const d = parseYYYYMMDDToLocalDate(dateOnly);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}


